import { useEffect, useState, useMemo } from 'react';
import api from '../../api';

// Shared hook: fetches NHL playoff team list + aggregate injuries once.
// Returns playoffTeams (Set of abbrevs) and injuryMap (Map of normalized name → status).
export default function usePlayerMeta() {
  const [playoffTeams, setPlayoffTeams] = useState(null);
  const [injuryMap, setInjuryMap] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const [playoff, injuries] = await Promise.all([
          api.fetchPlayoffTeams().catch(() => ({ teams: [] })),
          api.fetchAllInjuries().catch(() => ({ injuries: {} })),
        ]);
        if (!active) return;
        setPlayoffTeams(new Set(playoff?.teams || []));
        const map = new Map();
        const byTeam = injuries?.injuries || {};
        Object.values(byTeam).forEach((arr) => {
          (arr || []).forEach((inj) => {
            if (!inj?.name) return;
            map.set(normName(inj.name), inj.status || 'Out');
          });
        });
        setInjuryMap(map);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  return useMemo(() => ({ playoffTeams, injuryMap, loading }), [playoffTeams, injuryMap, loading]);
}

const normName = (s) =>
  s.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z ]/g, '').trim();

export { normName };

// "Day-to-day" -> DTD · "IR" / "Injured reserve" -> IR · anything else -> OUT (same tag as the draft room)
export function abbrevInjury(status) {
  const s = (status || '').toLowerCase();
  if (s.includes('day-to-day')) return 'DTD';
  if (s.includes('ir')) return 'IR';
  return 'OUT';
}
